import { Component, DestroyRef, inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { RouterLink, RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { pathNames } from './utilities/routing-path-utils';
import { DomSanitizer } from '@angular/platform-browser';
import { AppComponentEventsService } from './events/app-component-events.service';
import { AuthenticationService } from './services/authentication.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTreeModule } from '@angular/material/tree';
import { localStoragevarNames } from './utilities/local-storage-utils';
import { PaymentPlan } from './models/enums/payment-plan';
import { NavItem } from './models/views/nav-item';
import { SubscriptionHelper } from './classes/subscription-helper';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserService } from './services/user.service';

export const ZAPIER_ICON = 'zapier';
export const TELEGRAM_ICON = 'telegram';
export const NOTIFICATION_ICON = 'notification';
export const MISSED_CALL_ICON = 'missedcall';
export const SMS_ICON = 'sms';
export const GOOGLE_ICON = 'google';
export const CAL_ICON = 'cal';
export const GHL_ICON = 'ghl';
export const CALL_BACK_ICON = 'callback';
export const GOOGLE_SHEET_ICON = 'google_sheet'
export const MAKE_ICON = "make";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    LoginPageComponent,
    NgIf,
    NgFor,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatExpansionModule,
    MatIconModule,
    AsyncPipe,
    RouterOutlet,
    MatProgressSpinnerModule,
    RouterLink,
    MatTreeModule
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  shouldRenderNav: boolean = false;
  aiAgentConfigurationPath: string = pathNames.aiAgentConfigurationPath;
  paymentPlansPath: string = pathNames.paymentPlansPath;
  agencyPath: string = pathNames.subaccountPath;
  rebillingPath: string = pathNames.rebillingPath;
  userSettingsPath: string = pathNames.userSettingsPath;
  isWhitelabeledSubscribed: boolean = false;
  imageUrl: string | undefined = undefined;
  sideNav: NavItem[] = [];
  subscriptions: SubscriptionHelper;
  logoLoading:boolean = true;

  private breakpointObserver = inject(BreakpointObserver);

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  constructor(
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private appComponentEventService: AppComponentEventsService,
    private authService: AuthenticationService,
    private userService:UserService,
    private destroyRef: DestroyRef) {
    iconRegistry.addSvgIcon(GOOGLE_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/google-logo.svg'));
    iconRegistry.addSvgIcon(CAL_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/cal-logo.svg'));
    iconRegistry.addSvgIcon(GHL_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/ghl-logo.svg'));
    iconRegistry.addSvgIcon(ZAPIER_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/zapier-logo.svg'));
    iconRegistry.addSvgIcon(TELEGRAM_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/telegram-logo.svg'));
    iconRegistry.addSvgIcon(NOTIFICATION_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/notification-logo.svg'));
    iconRegistry.addSvgIcon(MISSED_CALL_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/call-back-settings-logo.svg'));
    iconRegistry.addSvgIcon(SMS_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/sms-logo.svg'));
    iconRegistry.addSvgIcon(GOOGLE_SHEET_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/google-sheets-icon.svg'));
    iconRegistry.addSvgIcon(CALL_BACK_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/phone-call-back-logo.svg'));
    iconRegistry.addSvgIcon(MAKE_ICON, sanitizer.bypassSecurityTrustResourceUrl('/assets/logos/make-logo.svg'));
    this.subscriptions = new SubscriptionHelper(this.destroyRef);
  }

  ngOnInit() {
    this.shouldRenderNav = this.authService.isAuthenticated();
    this.initSubscriptions();
    this.initSideNav();
    this.fetchLoggedUserAndSetLogo();
  }



  initSideNav() {
    this.checkIfUserWhiteLabeledSubscribed();

    this.sideNav = [
      { title: 'Dashboard', iconString: 'dashboard', routerLink: pathNames.adminDashboardPath, shouldRender: this.authService.isAdmin() },
      { title: 'Assistants', iconString: 'person_add_alt', routerLink: pathNames.aiAgentConfigurationPath, shouldRender: true },
      { title: 'Clients', iconString: 'groups_outline', routerLink: pathNames.subaccountPath, shouldRender: this.isWhitelabeledSubscribed },
      { title: 'Rebilling', iconString: 'storefront', routerLink: pathNames.rebillingPath, shouldRender: this.isWhitelabeledSubscribed },
      { title: 'Custom Domain', iconString: 'language', routerLink: pathNames.customDomain, shouldRender: this.isWhitelabeledSubscribed },
      { title: 'Plans', iconString: 'payment', routerLink: pathNames.paymentPlansPath, shouldRender: true },
      { title: 'Numbers', iconString: 'phone_in_talk', routerLink: pathNames.userPhoneNumbersPath, shouldRender: true },
      { title: 'Settings', iconString: 'settings', routerLink: pathNames.userSettingsPath, shouldRender: true }
    ]
  }

  checkIfUserWhiteLabeledSubscribed() {
    if (this.authService.isAuthenticated()) {
      const loggedUser = JSON.parse(localStorage.getItem(localStoragevarNames.user)!);
      this.isWhitelabeledSubscribed = loggedUser.paymentSettings && (loggedUser.paymentSettings.paymentPlan == PaymentPlan.WHITELABEL || loggedUser.paymentSettings.paymentPlan == PaymentPlan.DISCOUNTED);
    }
  }

  initSubscriptions() {
    this.subscribeToOnUserLoginEvent();
    this.subscribeToOnUserLogoutEvent();
    this.subscribeToOnNavRefreshEvent();
  }

  subscribeToOnNavRefreshEvent() {
    this.subscriptions.add(
      this.appComponentEventService.onNavRefreshEvent.subscribe(() => {
        this.initSideNav();
      }));
  }

  subscribeToOnUserLoginEvent() {
    this.subscriptions.add(
      this.appComponentEventService.onUserLogInEvent.subscribe(() => {
        this.shouldRenderNav = true;
        this.initSideNav();
      })
    );
  }

  subscribeToOnUserLogoutEvent() {
    this.subscriptions.add(
      this.appComponentEventService.onUserLogOutEvent.subscribe(() => {
        this.shouldRenderNav = false;
      })
    );
  }

  onLogout() {
    this.authService.logout();
  }

  private fetchLoggedUserAndSetLogo() {
    this.logoLoading = true;
    this.userService.fetchLoggedUser().subscribe({
      next: (data) => {
        this.logoLoading = false;
        if (data.mainAccount?.whiteLabelSettings) {
          this.imageUrl = data.mainAccount.whiteLabelSettings.logo;
        } else if (data.whiteLabelSettings != undefined) {
          this.imageUrl = data.whiteLabelSettings.logo;
        }
      },
      error:()=>{
        this.logoLoading = false;
      }
    });
  }
}
